import Vue from 'vue'
import VueResource from 'vue-resource'
import io from 'socket.io-client'
import IdoniaError from '~/error/IdoniaError'
import store from '~/store/store'
import bus from '~/bus.js'
import { v4 as uuidv4 } from 'uuid'
import { useStream } from '~/commons/stream'
import router from '~/router.js'

const {fetchStream: getStream} = useStream(bus)

Vue.use(VueResource)

export default {
  sockets: new Map(),
  config: {
    accountId: 0,
    idToken: '',
    token: '',
    apiUrl: '',
    wsUrl: '',
    wsID: '',
    authUrl: '',
    ssoUrl: '',
    initializing: true,
    destroyingSession: false
  },
  _addParams (url, extraParams) {
    const params = Object.assign({
      id_token: this.config.idToken,
      token: this.config.token,
      account_id: this.config.accountId,
      socket_id: this.config.wsID,
      _: Date.now()
    }, extraParams || {})
    const paramsString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&')

    return url.endsWith('?') ? url + paramsString : url + '?' + paramsString
  },
  getAuthHeaders: function (headers = {}) {
    if (this.config.idToken) {
      headers.Authorization = 'Bearer ' + this.config.idToken + ',' + this.config.wsID
    } else {
      const wsID = this.config.wsID || (store.state.notifications.guestSockets[router.currentRoute.params.sha] || {}).wsID || ''
      headers.Authorization = 'IDONIA token=' + this.config.token + ',account_id=' + this.config.accountId + ',socket_id="' + wsID + '"'
    }
    return headers
  },
  _prepareConfig (skipAuth, headers, params) {
    var config = {}
    config.emulateJSON = true
    headers = {
      'content-type': 'application/json',
      'x-idonia-correlation-id': uuidv4(),
      ...headers
    }
    if (!skipAuth && typeof headers.Authorization === 'undefined') {
      headers = this.getAuthHeaders(headers)
    }
    config.headers = headers
    config.params = Object.assign({}, params, { _: Date.now() })
    config.credentials = true
    return config
  },
  _handleStatus (resp, errorCallback) {
    const body = resp.body || {}
    if (this.config.initializing) {
      return
    }
    switch (resp.status) {
      case 401:
        console.error(new IdoniaError('Unauthorized', 3, IdoniaError.API_TYPE, 401))
        // DESTROYING SESSION
        if (this.config.destroyingSession) break
        const mlOrigin = store.getters['session/hasGuestAuthentication']
        store.dispatch('session/destroyGuestSession').finally(() => {
          if (store.getters['session/isAuthenticated'] || !mlOrigin) {
            store.dispatch('session/destroySession').then(() => {
              window.location.replace('/')
            })
          } else {
            store.dispatch('session/logout').then(() => {
              router.push(Vue.prototype.$config.defaultMagicLinkURL)
            })
          }
        })
        break
      case 403:
        console.warn(new IdoniaError('Forbidden', 3, IdoniaError.API_TYPE, 403))
        break
      case (resp.status >= 400 && resp.status < 500):
        console.error(new IdoniaError(body.error, 3, IdoniaError.API_TYPE, body.code))
        break
      default:
    }
    console.log('[_handleStatus] ', resp)
    errorCallback(resp)
  },
  post (endpoint, formData, successCallback, errorCallback, skipAuth, headers, onProgress) {
    if (this.config.destroyingSession) return
    successCallback = successCallback || (response => response)
    errorCallback = errorCallback || (response => response)
    skipAuth = skipAuth || false
    headers = headers || {}

    return new Promise((resolve, reject) => {
      const before = typeof onProgress === 'function' ? () => {} : xhr => { resolve(xhr) }
      onProgress = onProgress || (() => { })

      let config = this._prepareConfig(skipAuth, headers)
      config.emulateJSON = false
      config.progress = onProgress
      config.before = before
      Vue.http.post(this.config.apiUrl + endpoint, formData, config)
      .then(resp => {
        successCallback(resp)
        resolve(resp)
      }, (resp) => {
        this._handleStatus(resp, errorCallback)
        reject(resp)
      })
    })
  },
  get (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    params = params || {}
    successCallback = successCallback || (response => response)
    errorCallback = errorCallback || (response => response)
    skipAuth = skipAuth || false
    headers = headers || {}

    let config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.get(this.config.apiUrl + endpoint, config).then(successCallback, response => {
      this._handleStatus(response, errorCallback)
      return response
    })
  },
  download (endpoint, params) {
    this.downloadUrl(this._addParams(this.config.apiUrl + endpoint, params || {}))
  },
  async downloadUrl (url) {
    var frame = document.createElement('iframe')
    frame.src = url
    setTimeout(() => {
      document.body.removeChild(frame)
    }, 10000)
    document.body.appendChild(frame)
  },
  put (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    let config = this._prepareConfig(skipAuth, headers)
    config.emulateJSON = false
    return Vue.http.put(this.config.apiUrl + endpoint, params, config).then(successCallback, (resp) => {
      this._handleStatus(resp, errorCallback)
    })
  },
  delete (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    let config = this._prepareConfig(skipAuth, headers)
    return Vue.http.delete(this.config.apiUrl + endpoint, config).then(successCallback, (resp) => {
      this._handleStatus(resp, errorCallback)
    })
  },
  websocket () {
    if (!this.config.accountId || this.config.accountId === 0 || this.config.accountId === '0') {
      return null
    }
    if (!this.sockets || !this.sockets.has('account')) {
      let socket = io(this._addParams(this.config.wsUrl))
      this.sockets.set('account', socket)
    }
    bus.$emit('loadedSocket', this.sockets)
    return this.sockets.get('account')
  },
  guestWebsocket (token) {
    let socket = io(this.config.wsUrl + '?account_id=0&token=' + token)
    this.sockets.set('guest-' + token, socket)
    bus.$emit('loadedSocket', this.sockets)
    return socket
  },
  getWebsockets () {
    return this.sockets
  },
  putV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, data, skipAuth, headers, progress, before } = payload || {}

    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      progress === undefined &&
      before === undefined
    ) {
      params = payload
    }

    const config = Object.assign(this._prepareConfig(skipAuth, headers, params), {
      emulateJSON: false,
      progress: progress,
      before: before
    })

    return Vue.http.put(this.config.apiUrl + endpoint, data, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  postV2 (endpoint, payload) {
    if (this.config.destroyingSession && endpoint !== 'account/logout') return
    let { params, data, skipAuth, headers, progress, before } = payload || {}

    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      progress === undefined &&
      before === undefined
    ) {
      params = payload
    }

    const config = Object.assign(this._prepareConfig(skipAuth, headers, params), {
      emulateJSON: false,
      progress: progress,
      before: before
    })
    return Vue.http.post(this.config.apiUrl + endpoint, data, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  remoteGet (url, payload) {
    let { params, skipAuth, headers } = payload || {}

    if (params === undefined && skipAuth === undefined && headers === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.get(url, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  getV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, skipAuth, headers } = payload || {}

    if (params === undefined && skipAuth === undefined && headers === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.get(this.config.apiUrl + endpoint, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  deleteV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, skipAuth, headers } = payload || {}

    if (params === undefined && skipAuth === undefined && headers === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.delete(this.config.apiUrl + endpoint, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  fetchStream (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, data, skipAuth, headers, before, fetch, callback, eventID } = payload || {}
    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      before === undefined &&
      fetch === undefined &&
      callback === undefined &&
      eventID === undefined
    ) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    const url = new URL(this.config.apiUrl + endpoint)
    Object.keys(config.params).forEach(key => url.searchParams.append(key, config.params[key]))
    const streamConfig = {
      fetch: Object.assign(fetch || {}, {
        body: data,
        headers: config.headers,
        credentials: config.credentials ? 'include' : ''
      }),
      before,
      endpoint,
      eventID
    }
    return getStream(url.toString(), streamConfig, callback)
  }
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/api.js